import { useEffect, useRef, useState } from 'react';
import { defaultInvoicingSettings, invoiceLogoError, MAX_INVOICE_LOGO_BYTES, parseInvoicingSettings, type InvoicingSettings as Details } from '../../../shared/invoicingSettings';
import type { Brand } from '../../../shared/types';
import { api } from '../../lib/api';

export function InvoiceBrandMark({ brand, logo }: { brand: Brand; logo: string }) {
  return logo ? <img className="invoice-logo-image" src={logo} alt={`${brand === 'hsp' ? 'HSP' : 'Sheahan Studios'} logo`} />
    : brand === 'hsp' ? <span className="hsp-mark">HSP</span> : <span className="s-mark">S</span>;
}
export function InvoiceHeaderPreview({ details, brand }: { details: Details; brand: Brand }) {
  const identity = details.brands[brand];
  const container = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(0.7);
  useEffect(() => {
    const element = container.current;
    if (!element) return;
    const measure = () => setZoom(Math.min(1, element.clientWidth / 674));
    measure();
    const observer = new ResizeObserver(measure); observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return <div ref={container} className="invoice-header-preview"><div style={{ zoom }} className={`invdoc head-only brand-${brand}`}>
    <div className="hd"><div className="mark"><InvoiceBrandMark brand={brand} logo={identity.logo_data_url} />
      <div className="wm">{identity.business_name}<small>{identity.tagline}</small></div></div>
      <div className="hd-r"><div className="eyebrow">Tax invoice</div><h1>Invoice <span className="no">#0214</span></h1><span className="pill pill-unpaid">Awaiting payment</span></div></div>
    <div className="rule" />
    <div className="foot-line"><span className="biz">{identity.business_name}{details.abn ? ` · ABN ${details.abn}` : ''}</span><span>{details.footer_line}</span></div>
  </div></div>;
}
export function InvoicingSettingsForm({ initial, projectCounts, onSaved }: {
  initial: Details; projectCounts: Record<Brand, number>; onSaved?: (message: string) => void;
}) {
  const [details, setDetails] = useState(initial);
  const [saved, setSaved] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState(false);
  const uploadSequence = useRef({ hsp: 0, ss: 0 });
  const [uploading, setUploading] = useState({ hsp: false, ss: false });
  const changed = Object.keys(details).filter(key => JSON.stringify(details[key as keyof Details]) !== JSON.stringify(saved[key as keyof Details])).length;
  const dirty = changed > 0;
  const validation = parseInvoicingSettings(details);
  useEffect(() => {
    if (!savedMessage) return;
    const timer = setTimeout(() => setSavedMessage(false), 3500);
    return () => clearTimeout(timer);
  }, [savedMessage]);
  function shared<K extends keyof Details>(key: K, value: Details[K]) {
    setDetails(current => ({ ...current, [key]: value })); setSavedMessage(false); setError(null);
  }
  function brandField(brand: Brand, key: keyof Details['brands']['ss'], value: string) {
    setDetails(current => ({ ...current, brands: { ...current.brands, [brand]: { ...current.brands[brand], [key]: value } } }));
    setSavedMessage(false); setError(null);
  }
  async function logo(brand: Brand, file?: File) {
    if (!file || busy) return;
    const sequence = ++uploadSequence.current[brand];
    if (file.size > MAX_INVOICE_LOGO_BYTES) { setUploading(current => ({ ...current, [brand]: false })); setError('Logo must be 200 KB or smaller.'); return; }
    if (!['image/png', 'image/svg+xml'].includes(file.type)) { setUploading(current => ({ ...current, [brand]: false })); setError('Choose a PNG or SVG logo.'); return; }
    setUploading(current => ({ ...current, [brand]: true }));
    try {
      const data = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader(); reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(new Error('Could not read the logo. Try again.')); reader.readAsDataURL(file);
      });
      if (sequence !== uploadSequence.current[brand]) return;
      const logoError = invoiceLogoError(data);
      if (logoError) throw new Error(logoError);
      brandField(brand, 'logo_data_url', data);
    } catch (err) { setError(err instanceof Error ? err.message : 'Could not read the logo.'); }
    finally { if (sequence === uploadSequence.current[brand]) setUploading(current => ({ ...current, [brand]: false })); }
  }
  async function save() {
    if (validation.error) { setError(validation.error); return; }
    setBusy(true); setError(null);
    try {
      const response = await api<{ settings: Details }>('/api/admin/settings/invoicing', { method: 'PUT', json: details });
      setDetails(response.settings); setSaved(response.settings); setSavedMessage(true); onSaved?.('Invoicing details saved');
    } catch (err) { setError(err instanceof Error ? err.message : 'Could not save invoicing details.'); }
    finally { setBusy(false); }
  }
  const field = (key: Exclude<keyof Details, 'brands' | 'default_terms_days'>, label: string, mono = false, span = false) =>
    <div className={`f-field${span ? ' span-2' : ''}`}><label className="f-label" htmlFor={`inv-${key}`}>{label}</label>
      <input id={`inv-${key}`} className={`f-input${mono ? ' mono' : ''}`} value={details[key]} disabled={busy}
        type={key === 'accounts_email' ? 'email' : 'text'} inputMode={['abn', 'bsb', 'account_number'].includes(key) ? 'numeric' : undefined}
        onChange={e => shared(key, e.target.value)} /></div>;
  return <form className="invoicing-settings" onSubmit={e => { e.preventDefault(); void save(); }}>
    {error && <div className="error-text" role="alert">{error}</div>}
    <div className="settings-card inv-shared"><div className="sc-head"><span className="sc-title">Shared details</span><span className="sc-meta">both brands</span></div>
      <div className="sc-desc">Printed on every invoice whichever brand it carries.</div>
      <div className="form-grid inv-grid">
        {field('abn', 'ABN', true)}{field('bank_name', 'Bank name')}{field('bsb', 'BSB', true)}{field('account_number', 'Account number', true)}
        {field('account_name', 'Account name')}
        <div className="f-field"><label className="f-label" htmlFor="inv-default_terms_days">Default payment terms</label>
          <div className="inv-days"><input id="inv-default_terms_days" className="f-input mono" type="number" min={0} max={365} step={1}
            disabled={busy} value={Number.isNaN(details.default_terms_days) ? '' : details.default_terms_days}
            onChange={e => shared('default_terms_days', e.target.value === '' ? NaN : Number(e.target.value))} /><span>days</span></div>
          <span className="inv-hint">Clients can override this.</span></div>
        {field('accounts_email', 'Accounts email', true)}{field('footer_line', 'Footer line', false, true)}
      </div></div>
    <div className="inv-sec">Brands</div>
    <div className="settings-grid inv-brands">{(['hsp', 'ss'] as const).map(brand => <div key={brand} className="settings-card inv-brand">
      <div className="sc-head"><span className="sc-title">{brand === 'hsp' ? 'HSP' : 'Sheahan Studios'}</span><span className="sc-meta">{projectCounts[brand]} projects</span></div>
      <div className="form-grid">{(['business_name', 'tagline'] as const).map(key => <div key={key} className="f-field span-2">
        <label className="f-label" htmlFor={`inv-${brand}-${key}`}>{key === 'business_name' ? 'Business name' : 'Tagline'}</label>
        <input id={`inv-${brand}-${key}`} className="f-input" disabled={busy} value={details.brands[brand][key]} onChange={e => brandField(brand, key, e.target.value)} /></div>)}
        <div className="f-field span-2"><label className="f-label" htmlFor={`inv-${brand}-logo`}>Logo</label>
          <div className="inv-logo" onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); void logo(brand, e.dataTransfer.files[0]); }}>
            <div className={`invdoc brand-${brand} inv-logo-prev`}><div className="mark"><InvoiceBrandMark brand={brand} logo={details.brands[brand].logo_data_url} /></div></div>
            <div className="inv-logo-copy"><span>Drop an SVG or PNG, or <label className="inv-file-label" htmlFor={`inv-${brand}-logo`}>choose a file</label></span>
              <input id={`inv-${brand}-logo`} className="inv-file" type="file" accept="image/png,image/svg+xml" disabled={busy}
                onChange={e => { void logo(brand, e.target.files?.[0]); e.target.value = ''; }} />
              <small>200 KB maximum. Shown at 34 px on the invoice.</small>
              {uploading[brand] && <span role="status">Reading logo…</span>}
              {details.brands[brand].logo_data_url && <button type="button" className="inv-remove-logo" disabled={busy} onClick={() => { ++uploadSequence.current[brand]; setUploading(current => ({ ...current, [brand]: false })); brandField(brand, 'logo_data_url', ''); }}>Remove logo</button>}
            </div></div></div>
        <div className="f-field span-2"><label className="f-label" htmlFor={`inv-${brand}-reply_to`}>Reply-to email</label>
          <input id={`inv-${brand}-reply_to`} className="f-input mono" type="email" disabled={busy} value={details.brands[brand].reply_to} onChange={e => brandField(brand, 'reply_to', e.target.value)} /></div>
      </div></div>)}</div>
    <div className="inv-sec">Preview<span className="inv-live">{dirty ? 'live · unsaved' : 'saved'}</span></div>
    <div className="inv-previews">{(['hsp', 'ss'] as const).map(brand => <div key={brand} className="inv-prev">
      <div className="inv-preview-caption">{brand === 'hsp' ? 'HSP' : 'Sheahan Studios'} header</div>
      <InvoiceHeaderPreview details={details} brand={brand} /></div>)}</div>
    <div className="inv-save"><button className="btn primary" type="submit" disabled={busy || !dirty || !!validation.error || uploading.hsp || uploading.ss}>
      {busy ? 'Saving…' : savedMessage ? 'Saved ✓' : 'Save invoicing details'}</button>{dirty && <span className="inv-dirty">{changed} unsaved {changed === 1 ? 'change' : 'changes'}</span>}</div>
    {dirty && validation.error && <p className="error-text" role="alert">{validation.error}</p>}
    <p className="inv-save-note">Applies to invoices issued after you save. Invoices already issued keep the details they were sent with.</p>
    {savedMessage && <div className="inv-toast" role="status">✓ Invoicing details saved</div>}
  </form>;
}
export default function InvoicingSettings() {
  const [data, setData] = useState<{ settings: Details; projectCounts: Record<Brand, number> } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let active = true;
    setError(null);
    api<{ settings: Details; projectCounts: Record<Brand, number> }>('/api/admin/settings/invoicing')
      .then(response => { if (active) setData(response); }).catch(err => { if (active) setError(err.message); });
    return () => { active = false; };
  }, [retry]);
  if (error) return <div role="alert" className="error-text">{error} <button className="btn" onClick={() => setRetry(current => current + 1)}>Retry</button></div>;
  if (!data) return <p role="status">Loading invoicing details…</p>;
  return <InvoicingSettingsForm initial={data.settings ?? defaultInvoicingSettings()} projectCounts={data.projectCounts} />;
}
